import React, { useState } from 'react';
import AuthForm from './AuthForm';
import AuthField from './AuthField';
import AuthFormGroup from './AuthFormGroup';
import { AuthAPI } from '../../../api';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import * as Validate from '../../../components/Validate';

const DEFAULT_ERROR_MESSAGE = 'An error occurred creating your account.  Please try again later.';

const ProviderClaimProfileForm = ({onComplete, title, loginLink, provider}) => {
  const [email, setEmail] = useState('');
  const [emailWarning, setEmailWarning] = useState();
  const [username, setUsername] = useState('');
  const [usernameWarning, setUsernameWarning] = useState();
  const [firstName, setFirstName] = useState('');
  const [firstNameWarning, setFirstNameWarning] = useState();
  const [lastName, setLastName] = useState('');
  const [lastNameWarning, setLastNameWarning] = useState();
  const [contactPhone, setContactPhone] = useState('');
  const [contactPhoneWarning, setContactPhoneWarning] = useState();
  const [signupError, setSignupError] = useState();
  const [busy, setBusy] = useState(false);
  const [emailListAgreed, setEmailListAgreed] = useState(true);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTermsWarning, setShowTermsWarning] = useState(false);
  const [abn, setAbn] = useState('');
  const [abnWarning, setAbnWarning] = useState();
  const [entityName, setEntityName] = useState('');
  const [entityNameWarning, setEntityNameWarning] = useState();

  const signup = async () => {
    setSignupError();
    const valid = [

      await Validate.validateProviderEmail(email, setEmailWarning),
      await Validate.validateProviderUsername(username, setUsernameWarning),
      Validate.validateFirstName(firstName, setFirstNameWarning),
      Validate.validateLastName(lastName, setLastNameWarning),
      Validate.validatePhoneNumber(contactPhone, setContactPhoneWarning),
      Validate.validateAbn(abn, setAbnWarning),
      Validate.validateEntityName(entityName, setEntityNameWarning),
      validateTermsAgreed(),
    ];
    if (!valid.every(v=>v)) return;
    try {
      setBusy(true);
      await AuthAPI.providerClaimProfile({
        uuid: provider.uuid,
        email: email,
        username: username,
        first_name: firstName,
        last_name: lastName,
        contact_phone: contactPhone,
        abn: abn,
        entity_name: entityName,
      });
      setBusy(false);
      onComplete(email);
    } catch (err) {
      setBusy(false);
      console.error(err);  // eslint-disable-line no-console
      const message = (err.body && err.body.message) ? err.body.message : DEFAULT_ERROR_MESSAGE;
      setSignupError(message);
    }
  };

  const validateTermsAgreed = () => {
    if (!termsAgreed) {
      setShowTermsWarning(true);
      return false;
    }
    setShowTermsWarning(false);
    return true;
  };

  const emailListCheckboxHandler = () => {
    setEmailListAgreed(!emailListAgreed);
  };

  const termsCheckboxHandler = () => {
    const currentState = termsAgreed;
    setTermsAgreed(!currentState);
    if (!currentState) {
      setShowTermsWarning(false);
    }
  };

  return (
    <React.Fragment>
      <AuthForm
        buttonLabel="Sign Up"
        buttonAction={signup}
        busy={busy}
        error={signupError}
      >
        <Typography variant="h1" color="primary">
          {title}
        </Typography>

        <Typography variant="h3">
          After completing the sign up process you will be able to edit your organisation&apos;s displayed information and invite other users to this account.
        </Typography>

        <AuthFormGroup title="Contact Details">

          <AuthField
            id="firstname"
            placeholder="Your first name"
            label="First Name"
            autoComplete="given-name"
            type="text"
            name="firstname"
            value={firstName}
            onChange={setFirstName}
            error={firstNameWarning}
            onBlur={() => Validate.validateFirstName(firstName, setFirstNameWarning, true)}
          />

          <AuthField
            id="lastname"
            placeholder="Your last name"
            label="Last Name"
            autoComplete="family-name"
            type="text"
            name="lastname"
            value={lastName}
            onChange={setLastName}
            error={lastNameWarning}
            onBlur={() => Validate.validateLastName(lastName, setLastNameWarning, true)}
          />

          <AuthField
            id="email"
            placeholder="Your email address"
            label="Email"
            autoComplete="email"
            type="email"
            name="email"
            value={email}
            onChange={setEmail}
            error={emailWarning}
            onBlur={() => Validate.validateEmail(email, setEmailWarning, true)}
          />

          <AuthField
            id="contactphone"
            placeholder="Contact Telephone Number"
            label="Contact Telephone"
            autoComplete="tel-national"
            type="text"
            name="contactphone"
            value={contactPhone}
            onChange={setContactPhone}
            error={contactPhoneWarning}
            onBlur={() => Validate.validatePhoneNumber(contactPhone, setContactPhoneWarning, true)}
          />

          <AuthField
            id="username"
            placeholder="Your username (not your real name)"
            label="Username"
            autoComplete="username"
            type="text"
            name="username"
            value={username}
            onChange={setUsername}
            error={usernameWarning}
            onBlur={() => {
              const value = username.trim();
              setUsername(value);
              Validate.validateProviderUsername(value, setUsernameWarning, true);
            }}
          />
        </AuthFormGroup>
        <AuthFormGroup title="Company Details">

          <AuthField
            id="organisation"
            label="Organisation Name"
            autoComplete="organization"
            type="text"
            name="organisation"
            value={provider.name}
            disabled={true}
          />

          <AuthField
            id="abn"
            placeholder="Your organisation's ABN"
            label="ABN*"
            type="text"
            name="abn"
            value={abn}
            onChange={setAbn}
            error={abnWarning}
            onBlur={() => Validate.validateAbn(abn, setAbnWarning, true) && setAbn(Validate.formatAbn(abn))}
          />

          <AuthField
            id="entityName"
            placeholder="Your organisation's registered legal name"
            label="Registered Name*"
            type="text"
            name="entityName"
            value={entityName}
            onChange={setEntityName}
            error={entityNameWarning}
            onBlur={() => Validate.validateEntityName(entityName, setEntityNameWarning, true)}
          />
        </AuthFormGroup>

        <FormControlLabel
          control={<Checkbox checked={emailListAgreed} onChange={emailListCheckboxHandler} />}
          label="Keep me posted about updates, features and offers!"
        />
        <FormControlLabel
          control={<Checkbox checked={termsAgreed} onChange={termsCheckboxHandler} />}
          label={
            (
              <Typography variant="body2">
                I agree to Clickability&apos;s&nbsp;
                <Link component={RouterLink} to="/terms">
                  <b>Terms and Conditions</b>
                </Link>
                &nbsp;&&nbsp;
                <Link component={RouterLink} to="/privacy">
                  <b>Privacy Policy.</b>
                </Link>
              </Typography>
            )
          }
        />
        {showTermsWarning ? (<Alert severity="error">You must agree to the terms and privacy policy</Alert>) : null}
        <Typography variant="body2">
          You&apos;re about to sign up for our standard feature account. Become an <b>Accredited Clickability Provider</b> to increase your exposure to our growing number of participants and representatives searching Clickability each day.
        </Typography>
        <Typography variant="body2">
          Accreditation provides access to our premium features, including <b>Clickability&apos;s Matching Service</b>. One of our friendly team will be in contact soon to discuss how your business can benefit from our premium services.
        </Typography>

      </AuthForm>
      <Typography variant="body2">
        Already a member?&nbsp;
        <Link component={RouterLink} to={loginLink}>
          <b>Log in</b>
        </Link>.
      </Typography>
    </React.Fragment>
  );
};

ProviderClaimProfileForm.propTypes = {
  onComplete: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  loginLink: PropTypes.string.isRequired,
  provider: PropTypes.object.isRequired,
};

export default ProviderClaimProfileForm;
